/**
 * V4ArchitecturePanel — Plan 15 §2.1, §2.2, §2.4, §2.5
 *
 * Renders the Email Architecture V4 shape that the Funnel Manager could not
 * previously express: five lifecycle personas in journey order, and beneath them
 * the eight always-on diagnostic routes.
 *
 * Four things this puts in the product that only existed in the plan document:
 *
 *   §2.1  The persona journey, in order, by name rather than by slug.
 *   §2.2  Diagnostic routes as a distinct, visually subordinate but always-on
 *         section. A route blocked on a missing signal renders as BLOCKED with
 *         the reason — never as merely inactive. "It is not switched off; it
 *         cannot yet run, and the dashboard must say which."
 *   §2.4  Silence gaps. The artifact's central device is a red bar over
 *         stretches where nothing is sent; any gap over 14 days is drawn.
 *   §2.5  The cadence ladder, with a warning when a configuration inverts it.
 *         Effort earns contact; today it runs backwards and nothing noticed.
 *
 * Deliberately additive: this sits above the existing pipeline and step cards
 * rather than replacing them. Plan 15: "Do not replace the step card pattern or
 * the timing mechanism; extend them."
 */
import React, { useMemo } from 'react';
import { Box, Card, CardContent, Typography, Chip, Tooltip, LinearProgress, Alert } from '@mui/material';
import { EmailFunnelRow, EmailFunnelStepRow } from '../../../../services/api';
import { INK, SLATE, LINE } from './funnelShared';

const TEAL = '#1A5F7A';
const GOLD = '#D4AC0D';
const BURGUNDY = '#7D1935';
const FOREST = '#146152';
const CREAM = '#F9F3E9';

/** Journey order. A Member never moves backwards (master spec Rule 2). */
const LIFECYCLE_ORDER = [
  'margaret_never_started',
  'david_started_stalled',
  'sylvia_chapter_no_purchase',
  'tom_subscriber',
  'jean_cancelled',
];

const SHORT_NAME: Record<string, string> = {
  margaret_never_started: 'Margaret',
  david_started_stalled: 'David',
  sylvia_chapter_no_purchase: 'Sylvia',
  tom_subscriber: 'Tom',
  jean_cancelled: 'Jean',
};

const SUBTITLE: Record<string, string> = {
  margaret_never_started: 'never writes a word',
  david_started_stalled: 'stops halfway',
  sylvia_chapter_no_purchase: "writes it, doesn't buy",
  tom_subscriber: 'subscribes, pays',
  jean_cancelled: 'cancelled',
};

export function sendCount(f: EmailFunnelRow): number {
  return (f.steps || []).filter(s => s.step_type === 'send_email' || s.step_type === 'send_sms').length;
}

/** A route is BLOCKED, not inactive, when every one of its sends is held. */
export function blockedReason(f: EmailFunnelRow): string | null {
  const sends = (f.steps || []).filter(s => s.step_type === 'send_email');
  if (!sends.length) return null;
  const held = sends.filter(s => s.copy_state === 'held');
  if (held.length !== sends.length) return null;
  const reason = held.find(s => s.copy_state_reason)?.copy_state_reason || '';
  const first = reason.split(/[.—]/)[0]?.trim();
  return first ? first.slice(0, 120) : 'Awaiting a signal that does not exist yet';
}

function CopyStateBar({ f }: { f: EmailFunnelRow }) {
  const by = f.template_health?.by_state;
  const total = f.template_health?.total || 0;
  if (!by || !total) return null;
  const seg = (n: number, c: string, label: string) =>
    n > 0 ? (
      <Tooltip key={label} title={`${n} ${label}`}>
        <Box sx={{ width: `${(n / total) * 100}%`, bgcolor: c, height: 6 }} />
      </Tooltip>
    ) : null;
  return (
    <Box sx={{ display: 'flex', width: '100%', height: 6, borderRadius: 3, overflow: 'hidden', mt: 1 }}>
      {seg(by.ready, FOREST, 'ready')}
      {seg(by.stub, '#C9BFA8', 'stub — no copy yet')}
      {seg(by.held, BURGUNDY, 'held — cannot send')}
      {seg(by.rewrite_pending, GOLD, 'rewrite pending')}
      {seg(by.missing, '#B0413E', 'template missing')}
    </Box>
  );
}

function SilenceGaps({ gaps }: { gaps?: { from_day: number; to_day: number; days: number }[] }) {
  if (!gaps || !gaps.length) return null;
  const worst = Math.max(...gaps.map(g => g.days));
  return (
    <Tooltip title={gaps.map(g => `Day ${g.from_day} → ${g.to_day}: ${g.days} days of silence`).join(' · ')}>
      <Chip
        size="small"
        label={`${gaps.length} silent stretch${gaps.length > 1 ? 'es' : ''} · worst ${worst}d`}
        sx={{ bgcolor: '#F1E3E7', color: BURGUNDY, fontWeight: 700, fontSize: 11, height: 22 }}
      />
    </Tooltip>
  );
}

function FunnelCard({ f, index }: { f: EmailFunnelRow; index?: number }) {
  const blocked = blockedReason(f);
  const sends = sendCount(f);
  const short = SHORT_NAME[f.slug];
  return (
    <Card variant="outlined" sx={{ borderColor: LINE, flex: 1, minWidth: 210, bgcolor: '#fff' }}>
      <CardContent sx={{ p: 1.75, '&:last-child': { pb: 1.75 } }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, mb: 0.25 }}>
          {index != null && (
            <Typography sx={{ fontSize: 11, fontWeight: 800, color: SLATE }}>{index + 1}</Typography>
          )}
          <Typography sx={{ fontSize: 15, fontWeight: 800, color: INK }}>
            {short || f.name}
          </Typography>
        </Box>
        <Typography sx={{ fontSize: 12, color: SLATE, mb: 1 }}>
          {short ? SUBTITLE[f.slug] : f.description?.slice(0, 60)}
        </Typography>

        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 0.5 }}>
          <Chip size="small" label={`${sends} emails`}
                sx={{ bgcolor: CREAM, color: INK, fontWeight: 700, fontSize: 11, height: 22 }} />
          {blocked ? (
            <Chip size="small" label="BLOCKED"
                  sx={{ bgcolor: BURGUNDY, color: '#fff', fontWeight: 800, fontSize: 11, height: 22 }} />
          ) : (
            <Chip size="small" label={f.status}
                  sx={{ bgcolor: f.status === 'active' ? FOREST : '#EAEEF1',
                        color: f.status === 'active' ? '#fff' : SLATE,
                        fontWeight: 700, fontSize: 11, height: 22, textTransform: 'capitalize' }} />
          )}
          <SilenceGaps gaps={f.silence_gaps} />
        </Box>

        {blocked && (
          <Typography sx={{ fontSize: 11, color: BURGUNDY, mt: 0.5, lineHeight: 1.35 }}>
            {blocked}
          </Typography>
        )}
        <CopyStateBar f={f} />
        <Typography sx={{ fontSize: 10.5, color: SLATE, mt: 0.75 }}>
          {f.template_health?.ready ?? 0} of {f.template_health?.total ?? 0} have copy
        </Typography>
      </CardContent>
    </Card>
  );
}

export default function V4ArchitecturePanel({ flows }: { flows: EmailFunnelRow[] }) {
  const lifecycle = useMemo(
    () => LIFECYCLE_ORDER
      .map(slug => flows.find(f => f.slug === slug))
      .filter(Boolean) as EmailFunnelRow[],
    [flows],
  );
  const diagnostic = useMemo(
    () => flows.filter(f => f.funnel_class === 'diagnostic')
               .sort((a, b) => a.name.localeCompare(b.name)),
    [flows],
  );

  if (!lifecycle.length && !diagnostic.length) return null;

  // §2.5 — the cadence ladder. Effort earns contact, so each persona should
  // receive at least as many emails as the one before. Jean is excluded: she has
  // left, so she sits outside the effort ladder by design.
  const ladder = lifecycle.filter(f => f.slug !== 'jean_cancelled');
  const counts = ladder.map(sendCount);
  const inverted = counts.some((n, i) => i > 0 && n < counts[i - 1]);

  const totalReady = flows.reduce((a, f) => a + (f.template_health?.ready || 0), 0);
  const totalSends = flows.reduce((a, f) => a + (f.template_health?.total || 0), 0);

  return (
    <Box sx={{ mb: 3 }}>
      <Typography sx={{ fontSize: 18, fontWeight: 800, color: INK, mb: 0.25 }}>
        Email Architecture V4
      </Typography>
      <Typography sx={{ fontSize: 12.5, color: SLATE, mb: 1.5 }}>
        Five lifecycle funnels in journey order, and eight diagnostic routes running underneath them.
        A Member is in exactly one lifecycle funnel at a time; a diagnostic route may fire alongside
        it and takes precedence.
      </Typography>

      {/* Copy readiness — the gate on going live at all */}
      <Card variant="outlined" sx={{ borderColor: LINE, mb: 2, bgcolor: CREAM }}>
        <CardContent sx={{ p: 1.75, '&:last-child': { pb: 1.75 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <Typography sx={{ fontSize: 13, fontWeight: 800, color: INK }}>Copy readiness</Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 800, color: totalReady ? FOREST : BURGUNDY }}>
              {totalReady} of {totalSends} emails have copy
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={totalSends ? (totalReady / totalSends) * 100 : 0}
            sx={{ mt: 1, height: 8, borderRadius: 4, bgcolor: '#E7DECB',
                  '& .MuiLinearProgress-bar': { bgcolor: totalReady ? FOREST : BURGUNDY } }}
          />
          <Typography sx={{ fontSize: 11.5, color: SLATE, mt: 0.75 }}>
            A funnel does not activate because it is built. It activates because it has copy —
            the engine skips an empty template rather than sending a blank email.
          </Typography>
        </CardContent>
      </Card>

      {inverted && (
        <Alert severity="warning" sx={{ mb: 2, fontSize: 12.5 }}>
          <strong>Cadence ladder inverted.</strong> Effort should earn contact:{' '}
          {ladder.map((f, i) => `${SHORT_NAME[f.slug] || f.name} ${counts[i]}`).join(' → ')}.
          A persona who has done more is receiving fewer emails than one who has done less.
        </Alert>
      )}

      {/* §2.1 — the persona journey */}
      {lifecycle.length > 0 && (
        <>
          <Typography sx={{ fontSize: 12, fontWeight: 800, color: SLATE, letterSpacing: 0.6, mb: 0.75 }}>
            LIFECYCLE — THE JOURNEY
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 0.75 }}>
            {lifecycle.map((f, i) => <FunnelCard key={f.id} f={f} index={i} />)}
          </Box>
          {!inverted && counts.length > 1 && (
            <Typography sx={{ fontSize: 11.5, color: FOREST, mb: 2, fontWeight: 600 }}>
              ✓ Cadence ladder holds — {ladder.map((f, i) => `${SHORT_NAME[f.slug]} ${counts[i]}`).join(' ≤ ')}
            </Typography>
          )}
        </>
      )}

      {/* §2.2 — the diagnostic layer */}
      {diagnostic.length > 0 && (
        <>
          <Typography sx={{ fontSize: 12, fontWeight: 800, color: SLATE, letterSpacing: 0.6, mt: 1, mb: 0.75 }}>
            DIAGNOSTIC — ALWAYS ON, TAKES PRECEDENCE
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {diagnostic.map(f => <FunnelCard key={f.id} f={f} />)}
          </Box>
          <Typography sx={{ fontSize: 11.5, color: SLATE, mt: 1 }}>
            A route marked <strong style={{ color: BURGUNDY }}>BLOCKED</strong> is not switched off —
            it cannot yet run, because the signal it listens for does not exist. The reason is shown
            on the card.
          </Typography>
        </>
      )}
    </Box>
  );
}
